/**
 * T47 — Apple StoreKit entitlement auto-sync.
 *
 * The iOS app purchases / restores via the StoreKitDirect Capacitor
 * plugin, which bypasses RevenueCat. RC has zero record of the
 * purchase, so the existing `/api/iap/sync` endpoint (which queries
 * RC) cannot flip the user's plan. This helper bridges that gap by
 * POSTing the native StoreKit transaction payload directly to the
 * `/api/iap/apple/apply` backend endpoint.
 *
 * Trigger sites (callers):
 *   - `auth.signInWithApple()`  — silent, immediately after sign-in
 *   - `App.jsx` resume handler  — silent, on every foreground
 *   - `usePaywallPurchase.buyIOS()`  — after a successful purchase
 *   - `usePaywallPurchase.onRestore()` — after a successful restore
 *
 * `silent=true` callers use `getEntitlements()` (no Apple ID prompt);
 * the explicit Restore button path passes the already-fetched
 * `restored` array directly to `applyTransactions()`.
 *
 * T49 — Two additions for App Review reliability:
 *   1) The latest StoreKit transactions are mirrored into the local
 *      entitlementCache so the device knows it's Pro even if the
 *      backend POST fails or is slow.
 *   2) The last sync result (success or failure) is persisted to
 *      localStorage so the Connection Diagnostics screen can surface
 *      it without re-running the probe.
 */

import api from "./api";
import { isIOS } from "./platform";
import { getEntitlements } from "./storekitDirect";
import { persistEntitlementFromTransactions } from "./entitlementCache";

const LS_LAST_SYNC_KEY = "tiplingo_last_iap_sync_v1";

function _writeLastSync(payload) {
  try {
    localStorage.setItem(
      LS_LAST_SYNC_KEY,
      JSON.stringify({ ...payload, at: new Date().toISOString() }),
    );
  } catch { /* non-fatal */ }
}

export function getLastIapSyncResult() {
  try {
    const raw = localStorage.getItem(LS_LAST_SYNC_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * POST a list of StoreKit transactions to the backend. Returns the
 * server's `{ synced, active, expires, product_id }` payload, OR
 * `null` if there were no eligible transactions to send. Safe to
 * call from anywhere — swallows the network error, never throws.
 */
export async function applyTransactions(transactions) {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return null;
  }
  // T49 — Always persist locally FIRST so Pro is unlocked even if
  // the network POST below fails. Backend reconciles on next sync.
  persistEntitlementFromTransactions(transactions);
  try {
    const { data } = await api.post(
      `/iap/apple/apply?_t=${Date.now()}`,
      { transactions },
      { headers: { "Cache-Control": "no-store" } },
    );
    _writeLastSync({ ok: true, source: "applyTransactions", data });
    return data;
  } catch (e) {
    _writeLastSync({
      ok: false,
      source: "applyTransactions",
      error: {
        status: e?.response?.status ?? null,
        message: e?.message || "network error",
      },
    });
    if (process.env.NODE_ENV !== "production") {
      console.warn("[T47] /iap/apple/apply failed:", e?.response?.status, e?.message);
    }
    return null;
  }
}

/**
 * Silent end-to-end auto-sync. Native probe → POST to backend → caller
 * decides whether to refresh user state.
 *
 *   - Skips on non-iOS (web has its own RC path).
 *   - Skips when no transactions present locally (clean uninstall,
 *     never purchased, etc.) — returns null, no toast, no error.
 *   - Returns the backend's apply payload on success so the caller can
 *     conditionally refresh.
 *
 * Designed to be called liberally — every sign-in success, every app
 * resume. The backend `_apply_entitlement` is idempotent ($set under
 * the same user_id), so repeated calls are cheap.
 */
export async function syncAppleEntitlement() {
  if (!isIOS()) return null;
  let result;
  try {
    result = await getEntitlements();
  } catch (e) {
    _writeLastSync({
      ok: false,
      source: "getEntitlements",
      error: { message: e?.message || "native error", code: e?.code || null },
    });
    if (process.env.NODE_ENV !== "production") {
      console.warn("[T47] getEntitlements failed:", e?.code, e?.message);
    }
    return null;
  }
  const txs = Array.isArray(result?.restored) ? result.restored : [];
  if (txs.length === 0) {
    _writeLastSync({ ok: true, source: "getEntitlements", data: { transactions: 0 } });
    return null;
  }
  return await applyTransactions(txs);
}
